/**
 * Secure Storage Utility
 * 
 * Provides secure localStorage operations with:
 * - Automatic expiration
 * - Data encryption for sensitive fields
 * - Cleanup on logout
 * - Size monitoring
 * 
 * Usage:
 *   import { secureStorage } from '@/utils/secureStorage';
 *   secureStorage.set('key', data, 3600); // expires in 1 hour
 */

import { devWarn, logError } from './secureLogger';

interface StorageItem<T> {
  value: T;
  expires?: number;
  timestamp: number;
  encrypted?: boolean;
}

const STORAGE_PREFIX = 'fusion_';
const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB

// Simple XOR encryption key (in production, use a proper encryption library)
const ENCRYPTION_KEY = 'fusion-secure-2024';

// Fields that should be encrypted
const SENSITIVE_FIELDS = ['email', 'mobile', 'personInCharge', 'phone', 'password', 'token', 'apiKey'];

class SecureStorage {
  /**
   * Simple XOR encryption/decryption
   * NOTE: This is basic obfuscation. For production, use a proper encryption library like crypto-js
   */
  private encrypt(text: string): string {
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(text.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length));
    }
    return btoa(result); // Base64 encode
  }

  private decrypt(encrypted: string): string {
    try {
      const decoded = atob(encrypted); // Base64 decode
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(decoded.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length));
      }
      return result;
    } catch {
      return encrypted; // Return as-is if decryption fails
    }
  }

  /**
   * Check if a value contains sensitive data that should be encrypted
   */
  private containsSensitiveData(value: any): boolean {
    if (typeof value !== 'object' || value === null) return false;
    
    const checkObject = (obj: any): boolean => {
      for (const key in obj) {
        const lowerKey = key.toLowerCase();
        if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()))) {
          return true;
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (checkObject(obj[key])) return true;
        }
      }
      return false;
    };
    
    return checkObject(value);
  }

  /**
   * Encrypt sensitive fields in an object
   */
  private encryptSensitiveFields(value: any): any {
    if (typeof value !== 'object' || value === null) return value;
    
    const encrypted = Array.isArray(value) ? [...value] : { ...value };
    
    for (const key in encrypted) {
      const lowerKey = key.toLowerCase();
      if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()))) {
        if (typeof encrypted[key] === 'string') {
          encrypted[key] = this.encrypt(encrypted[key]);
        }
      } else if (typeof encrypted[key] === 'object' && encrypted[key] !== null) {
        encrypted[key] = this.encryptSensitiveFields(encrypted[key]);
      }
    }
    
    return encrypted;
  }

  /**
   * Decrypt sensitive fields in an object
   */
  private decryptSensitiveFields(value: any): any {
    if (typeof value !== 'object' || value === null) return value;
    
    const decrypted = Array.isArray(value) ? [...value] : { ...value };
    
    for (const key in decrypted) {
      const lowerKey = key.toLowerCase();
      if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()))) {
        if (typeof decrypted[key] === 'string') {
          decrypted[key] = this.decrypt(decrypted[key]);
        }
      } else if (typeof decrypted[key] === 'object' && decrypted[key] !== null) {
        decrypted[key] = this.decryptSensitiveFields(decrypted[key]);
      }
    }
    
    return decrypted;
  }

  /**
   * Set item with optional expiration (in seconds) and automatic encryption for sensitive data
   */
  set<T>(key: string, value: T, expiresInSeconds?: number): void {
    try {
      const shouldEncrypt = this.containsSensitiveData(value);
      const processedValue = shouldEncrypt ? this.encryptSensitiveFields(value) : value;
      
      const item: StorageItem<T> = {
        value: processedValue,
        timestamp: Date.now(),
        expires: expiresInSeconds ? Date.now() + (expiresInSeconds * 1000) : undefined,
        encrypted: shouldEncrypt
      };

      const serialized = JSON.stringify(item);
      
      // Check size
      if (serialized.length > MAX_STORAGE_SIZE) {
        devWarn(`Storage item ${key} exceeds maximum size`);
        return;
      }

      localStorage.setItem(STORAGE_PREFIX + key, serialized);
    } catch (error) {
      logError(`Failed to set storage item ${key}`, error);
    }
  }

  /**
   * Get item, returns null if expired or not found, automatically decrypts if encrypted
   */
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(STORAGE_PREFIX + key);
      if (!item) return null;

      const parsed: StorageItem<T> = JSON.parse(item);

      // Check expiration
      if (parsed.expires && Date.now() > parsed.expires) {
        this.remove(key);
        return null;
      }

      // Decrypt if necessary
      const value = parsed.encrypted ? this.decryptSensitiveFields(parsed.value) : parsed.value;
      return value;
    } catch (error) {
      logError(`Failed to get storage item ${key}`, error);
      return null;
    }
  }

  /**
   * Remove item
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch (error) {
      logError(`Failed to remove storage item ${key}`, error);
    }
  }

  /**
   * Clear all prefixed items
   */
  clearAll(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      logError('Failed to clear storage', error);
    }
  }

  /**
   * Clear all campaign-related data
   */
  clearCampaignData(): void {
    try {
      const campaignKeys = [
        'formData',
        'selectedLocations',
        'devices',
        'duration',
        'budgetCalculations',
        'creativeUrls',
        'campaignName',
        'startDate',
        'endDate',
        'currentCampaignId',
        'showThankYou',
        'canceledPayment',
        'payment-cancel-flag'
      ];

      campaignKeys.forEach(key => {
        localStorage.removeItem(key);
        this.remove(key);
      });

      // Also remove any keys with email-specific budget calculations
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (key.includes('budgetCalculations_') || key.includes('campaign_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      logError('Failed to clear campaign data', error);
    }
  }

  /**
   * Clean up expired items
   */
  cleanExpired(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(STORAGE_PREFIX)) {
          const item = localStorage.getItem(key);
          if (!item) return;

          try {
            const parsed: StorageItem<any> = JSON.parse(item);
            if (parsed.expires && Date.now() > parsed.expires) {
              localStorage.removeItem(key);
            }
          } catch {
            // Invalid format, skip
          }
        }
      });
    } catch (error) {
      logError('Failed to clean expired items', error);
    }
  }

  /**
   * Get storage usage in bytes
   */
  getUsage(): number {
    try {
      let total = 0;
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          total += key.length + item.length;
        }
      });
      return total;
    } catch {
      return 0;
    }
  }

  /**
   * Check if storage is near capacity
   */
  isNearCapacity(): boolean {
    return this.getUsage() > MAX_STORAGE_SIZE * 0.8;
  }

  /**
   * Initialize storage (clean expired items and check capacity)
   * Call this manually after app initialization
   */
  initialize(): void {
    if (typeof window !== 'undefined') {
      this.cleanExpired();
      
      // Warn if near capacity
      if (this.isNearCapacity()) {
        devWarn('localStorage is near capacity. Consider clearing old data.');
      }
    }
  }
}

export const secureStorage = new SecureStorage();

// Defer initialization until after React is ready
if (typeof window !== 'undefined') {
  // Use setTimeout to ensure this runs after all module imports
  setTimeout(() => {
    secureStorage.initialize();
  }, 0);
}